import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Car,
  ChevronLeft,
  Save,
  Plus,
  Edit2,
  Trash2,
  X,
  GripVertical,
  Loader2,
  DollarSign,
  Eye,
  EyeOff,
  Copy,
  Settings,
  Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';

// Mock vehicle classes
const mockClasses = [
  {
    id: 1,
    class_code: '410_SPRINT',
    class_name: '410 Sprint Car',
    class_region: 'National',
    display_order: 1,
    is_active: true,
    registration_fee: 150.00,
    color: '#0c7abf',
    enrolled_count: 156
  },
  {
    id: 2,
    class_code: '360_SPRINT',
    class_name: '360 Sprint Car',
    class_region: 'Regional',
    display_order: 2,
    is_active: true,
    registration_fee: 125.00,
    color: '#E31837',
    enrolled_count: 89
  },
  {
    id: 3,
    class_code: 'LATE_MODEL',
    class_name: 'Late Model',
    class_region: 'National',
    display_order: 3,
    is_active: true,
    registration_fee: 150.00,
    color: '#1E4D8C',
    enrolled_count: 124
  },
  {
    id: 4,
    class_code: 'BB_MOD',
    class_name: 'Big Block Modified',
    class_region: 'Northeast',
    display_order: 4,
    is_active: true,
    registration_fee: 150.00,
    color: '#FFD100',
    enrolled_count: 78
  },
  {
    id: 5,
    class_code: '358_MOD',
    class_name: '358 Modified',
    class_region: 'Northeast',
    display_order: 5,
    is_active: true,
    registration_fee: 125.00,
    color: '#FF6600',
    enrolled_count: 65
  },
  {
    id: 6,
    class_code: 'UMP_MOD',
    class_name: 'UMP Modified',
    class_region: 'Midwest',
    display_order: 6,
    is_active: false,
    registration_fee: 100.00,
    color: '#00A651',
    enrolled_count: 0
  }
];

const regionOptions = [
  { id: 'national', name: 'National' },
  { id: 'northeast', name: 'Northeast' },
  { id: 'midwest', name: 'Midwest' },
  { id: 'southeast', name: 'Southeast' },
  { id: 'southwest', name: 'Southwest' },
  { id: 'west', name: 'West' }
];

const colorOptions = [
  '#0c7abf', '#1E4D8C', '#E31837', '#FFD100', '#FF6600',
  '#00A651', '#9B59B6', '#34495E', '#C12B2A', '#4A90A4'
];

function ClassCard({ vehicleClass, onEdit, onDelete, onToggleActive, onDragStart, onDragOver, onDrop }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, vehicleClass.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, vehicleClass.id)}
      className={`p-4 rounded-xl border-2 bg-white transition-all ${
        vehicleClass.is_active
          ? 'border-slate-200 hover:border-slate-300'
          : 'border-dashed border-slate-200 opacity-60'
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Drag Handle */}
        <div className="cursor-grab text-slate-300 hover:text-slate-500">
          <GripVertical className="w-5 h-5" />
        </div>

        {/* Color Indicator */}
        <div
          className="w-3 h-12 rounded-full flex-shrink-0"
          style={{ backgroundColor: vehicleClass.color }}
        />

        {/* Class Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-800">{vehicleClass.class_name}</h3>
            {!vehicleClass.is_active && (
              <Badge variant="neutral" size="sm">Inactive</Badge>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
              {vehicleClass.class_code}
            </span>
            {vehicleClass.class_region && (
              <span>{vehicleClass.class_region}</span>
            )}
            <span className="flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5" />
              {vehicleClass.registration_fee.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Enrolled Count */}
        <div className="text-center px-4">
          <div className="text-xl font-bold text-slate-800">{vehicleClass.enrolled_count}</div>
          <div className="text-xs text-slate-500">enrolled</div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(vehicleClass)}
            title={vehicleClass.is_active ? 'Deactivate' : 'Activate'}
          >
            {vehicleClass.is_active ? (
              <Eye className="w-4 h-4 text-emerald-500" />
            ) : (
              <EyeOff className="w-4 h-4 text-slate-400" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(vehicleClass)}
            title="Edit class"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          {vehicleClass.enrolled_count === 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(vehicleClass)}
              title="Delete class"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ClassEditModal({ vehicleClass, onSave, onClose }) {
  const isNew = !vehicleClass?.id;
  const [formData, setFormData] = useState({
    class_code: vehicleClass?.class_code || '',
    class_name: vehicleClass?.class_name || '',
    class_region: vehicleClass?.class_region || '',
    registration_fee: vehicleClass?.registration_fee || 0,
    color: vehicleClass?.color || colorOptions[0],
    is_active: vehicleClass?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onSave({
      ...vehicleClass,
      ...formData,
      id: vehicleClass?.id || Date.now(),
      display_order: vehicleClass?.display_order || 999,
      enrolled_count: vehicleClass?.enrolled_count || 0
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {isNew ? 'Add Vehicle Class' : 'Edit Vehicle Class'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Class Name"
              value={formData.class_name}
              onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
              placeholder="e.g., 410 Sprint Car"
              required
            />
            <Input
              label="Class Code"
              value={formData.class_code}
              onChange={(e) => setFormData({ ...formData, class_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
              placeholder="e.g., 410_SPRINT"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Region"
              value={formData.class_region}
              onChange={(e) => setFormData({ ...formData, class_region: e.target.value })}
            >
              <option value="">All Regions</option>
              {regionOptions.map((region) => (
                <option key={region.id} value={region.name}>
                  {region.name}
                </option>
              ))}
            </Select>
            <Input
              label="Registration Fee"
              type="number"
              min={0}
              step={0.01}
              value={formData.registration_fee}
              onChange={(e) => setFormData({ ...formData, registration_fee: parseFloat(e.target.value) || 0 })}
              leftIcon={DollarSign}
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Class Color
            </label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    formData.color === color
                      ? 'ring-2 ring-offset-2 ring-slate-400 scale-110'
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-rms-accent focus:ring-rms-accent"
            />
            <label htmlFor="is_active" className="text-sm text-slate-700">
              Class is active and available for registration
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isNew ? 'Add Class' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ClassesConfig() {
  const { seriesSlug } = useParams();
  const currentSeries = useSeries();
  const api = useApi();

  const [classes, setClasses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const { isLoading: loading } = useQuery({
    queryKey: ['classes', currentSeries?.dbId],
    queryFn: async () => {
      const result = await api.series.getClasses(currentSeries.dbId);
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load classes');
      const classesData = [];
      if (result.data.primaryClass) {
        classesData.push({
          id: result.data.primaryClass.id,
          class_code: result.data.primaryClass.class_code,
          class_name: result.data.primaryClass.class_name,
          class_region: 'Primary',
          display_order: 1,
          is_active: true,
          registration_fee: 150.00,
          color: result.data.primaryClass.color || '#0c7abf',
          enrolled_count: result.data.primaryClass.enrolled_count || 0
        });
      }
      setClasses(classesData);
      setAvailableClasses(result.data.availableClasses || []);
      return result.data;
    },
    enabled: !!currentSeries?.dbId,
  });

  const handleSaveClass = (savedClass) => {
    if (editingClass) {
      setClasses(classes.map(c => c.id === savedClass.id ? savedClass : c));
    } else {
      setClasses([...classes, savedClass]);
    }
    setEditingClass(null);
    setShowCreateModal(false);
  };

  const handleDeleteClass = (vehicleClass) => {
    if (window.confirm(`Delete "${vehicleClass.class_name}"? This action cannot be undone.`)) {
      setClasses(classes.filter(c => c.id !== vehicleClass.id));
    }
  };

  const handleToggleActive = (vehicleClass) => {
    setClasses(classes.map(c =>
      c.id === vehicleClass.id ? { ...c, is_active: !c.is_active } : c
    ));
  };

  // Drag and drop handlers
  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (draggedId === targetId) return;

    const newClasses = [...classes];
    const draggedIndex = newClasses.findIndex(c => c.id === draggedId);
    const targetIndex = newClasses.findIndex(c => c.id === targetId);

    const [draggedItem] = newClasses.splice(draggedIndex, 1);
    newClasses.splice(targetIndex, 0, draggedItem);

    // Update display_order
    newClasses.forEach((c, index) => {
      c.display_order = index + 1;
    });

    setClasses(newClasses);
    setDraggedId(null);
  };

  const activeClasses = classes.filter(c => c.is_active);
  const inactiveClasses = classes.filter(c => !c.is_active);
  const totalEnrolled = classes.reduce((sum, c) => sum + c.enrolled_count, 0);

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Vehicle Classes</h1>
            <p className="text-slate-500 mt-1">
              Configure vehicle classes for {currentSeries?.name || 'series'}
            </p>
          </div>
          <Button leftIcon={Plus} onClick={() => setShowCreateModal(true)}>
            Add Class
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Classes List */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Active Classes</CardTitle>
              <CardDescription>
                Drag to reorder. {activeClasses.length} active classes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : activeClasses.length === 0 ? (
                <div className="text-center py-8">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No active classes configured</p>
                  <Button
                    variant="secondary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    Add First Class
                  </Button>
                </div>
              ) : (
                activeClasses.map((vehicleClass) => (
                  <ClassCard
                    key={vehicleClass.id}
                    vehicleClass={vehicleClass}
                    onEdit={setEditingClass}
                    onDelete={handleDeleteClass}
                    onToggleActive={handleToggleActive}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Inactive Classes */}
          {inactiveClasses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-slate-500">Inactive Classes</CardTitle>
                <CardDescription>
                  {inactiveClasses.length} inactive classes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {inactiveClasses.map((vehicleClass) => (
                  <ClassCard
                    key={vehicleClass.id}
                    vehicleClass={vehicleClass}
                    onEdit={setEditingClass}
                    onDelete={handleDeleteClass}
                    onToggleActive={handleToggleActive}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Class Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm text-slate-600">Total Classes</span>
                  <span className="text-lg font-bold text-slate-800">{classes.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <span className="text-sm text-emerald-700">Active Classes</span>
                  <span className="text-lg font-bold text-emerald-700">{activeClasses.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-sm text-slate-600">Total Enrolled</span>
                  <span className="text-lg font-bold text-slate-800">{totalEnrolled}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="secondary"
                className="w-full justify-start"
                leftIcon={Copy}
                onClick={() => {
                  alert('Import from previous season functionality coming soon');
                }}
              >
                Import from Previous Season
              </Button>
              <Button
                variant="secondary"
                className="w-full justify-start"
                leftIcon={DollarSign}
                onClick={() => {
                  const newFee = prompt('Set registration fee for all classes:', '150.00');
                  if (newFee !== null) {
                    const fee = parseFloat(newFee) || 0;
                    setClasses(classes.map(c => ({ ...c, registration_fee: fee })));
                  }
                }}
              >
                Bulk Update Fees
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:bg-red-50"
                leftIcon={EyeOff}
                onClick={() => {
                  if (window.confirm('Deactivate all classes with 0 enrollments?')) {
                    setClasses(classes.map(c =>
                      c.enrolled_count === 0 ? { ...c, is_active: false } : c
                    ));
                  }
                }}
              >
                Deactivate Empty Classes
              </Button>
            </CardContent>
          </Card>

          {/* Enrollment by Class */}
          <Card>
            <CardHeader>
              <CardTitle>Enrollment by Class</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activeClasses
                  .sort((a, b) => b.enrolled_count - a.enrolled_count)
                  .slice(0, 5)
                  .map((vehicleClass) => {
                    const percentage = totalEnrolled > 0
                      ? Math.round((vehicleClass.enrolled_count / totalEnrolled) * 100)
                      : 0;
                    return (
                      <div key={vehicleClass.id}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-700">{vehicleClass.class_name}</span>
                          <span className="text-slate-500">{vehicleClass.enrolled_count}</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: vehicleClass.color
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {(editingClass || showCreateModal) && (
        <ClassEditModal
          vehicleClass={editingClass}
          onSave={handleSaveClass}
          onClose={() => {
            setEditingClass(null);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}
